import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Trophy, Crown, MapPin, Users, Calendar, Flame, Loader2, ChevronRight, Radio, Eye, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import useArenaxRealtime from './hooks/useArenaxRealtime';
import ArenaxReactionDock from './ArenaxReactionDock';
import ArenaxRivalryBadge from './ArenaxRivalryBadge';
import ArenaxComebackCallout from './ArenaxComebackCallout';
import ArenaxStoryInline from './ArenaxStoryInline';
import ArenaxLiveViewers from './ArenaxLiveViewers';
import ArenaxSpectatorStream from './ArenaxSpectatorStream';
import { TournamentAuditDrawer } from '@/components/arenax/LifecycleAuditDrawer';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Phase 2H — Tournament detail.
 * Renders the bracket (knockout) or standings (round-robin), surfaces Defending
 * Champions, and gives the creator a "Generate bracket" CTA when ready.
 */
export default function ArenaxTournamentDetail() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [mySquads, setMySquads] = useState([]);
  const [selectedSquadId, setSelectedSquadId] = useState(null);
  const [joining, setJoining] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);  // StreamX Sprint 3c — tournament lifecycle drawer

  const reload = () => {
    setLoading(true);
    api.get(`/arenax/tournaments/${tournamentId}`)
      .then(r => setT(r.data))
      .catch(() => setT(null))
      .finally(() => setLoading(false));
  };

  // Phase 2J — fetch the auto-composed recap when the tournament completes
  const [recap, setRecap] = useState(null);
  useEffect(() => {
    if (!tournamentId || t?.status !== 'completed') return;
    api.get(`/arenax/tournaments/${tournamentId}/recap`)
      .then(r => setRecap(r.data))
      .catch(() => setRecap(null));
  }, [tournamentId, t?.status]);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tournamentId]);

  // Phase 2I — subscribe to tournament-wide realtime channel for live bracket animation.
  // Phase 2J Realtime Phase 3 — use the resolved UUID (t?.id) rather than the URL param,
  // since slug-based URLs would never receive broadcasts (backend keys channels by UUID).
  const rt = useArenaxRealtime({ channel: 'tournament', channelId: t?.id, enabled: !!t?.id });
  // Auto-reload on match completion event (auto-advance happens server-side)
  useEffect(() => {
    if (!rt.tournamentEvent) return;
    if (rt.tournamentEvent.type === 'match_completed') {
      reload();
    }
    // eslint-disable-next-line
  }, [rt.tournamentEvent]);

  useEffect(() => {
    if (!user) return;
    api.get('/arenax/squads/mine')
      .then(r => {
        const captain = (r.data.squads || []).filter(s => s.captain_id === user.id);
        setMySquads(captain);
        if (captain[0]) setSelectedSquadId(captain[0].id);
      })
      .catch(() => {});
  }, [user]);

  const onJoin = async () => {
    if (!selectedSquadId) { toast.error('Pick a squad first'); return; }
    setJoining(true);
    try {
      await api.post(`/arenax/tournaments/${tournamentId}/join`, { squad_id: selectedSquadId });
      toast.success('Squad registered!');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not register');
    } finally { setJoining(false); }
  };

  const onGenerateBracket = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/arenax/tournaments/${tournamentId}/generate-bracket`);
      toast.success(`Bracket locked — ${r.data.matches_count} matches scheduled`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate bracket');
    } finally { setGenerating(false); }
  };

  if (loading) {
    return (
      <ArenaxLayout>
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="rounded-2xl border border-slate-800 h-72 animate-pulse" />
        </div>
      </ArenaxLayout>
    );
  }
  if (!t) {
    return (
      <ArenaxLayout>
        <div className="max-w-6xl mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-black text-slate-100">Tournament not found.</h2>
          <button onClick={() => navigate('/arenax/tournaments')}
            className="mt-4 px-4 py-2 rounded-lg text-sm font-bold text-slate-950 bg-amber-300">
            Back to tournaments
          </button>
        </div>
      </ArenaxLayout>
    );
  }

  const isCreator = user && t.created_by === user.id;
  const isSuperAdmin = user?.role === 'super_admin';
  // StreamX Sprint 3c — super_admin gets audit visibility on EVERY tournament
  // so they can investigate disputes / cancellations / cascade effects.  Creators
  // see it on their own tournaments as before.
  const canViewHistory = isCreator || isSuperAdmin;
  const alreadyRegistered = t.squads?.some(s => mySquads.find(ms => ms.id === s.squad_id));
  const canJoin = user && !alreadyRegistered && t.status === 'registration' && mySquads.length > 0;
  const canGenerate = isCreator && t.status === 'registration' && t.registered_count >= 2;

  return (
    <ArenaxLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="arenax-tournament-detail">
        {/* Header */}
        <div className="rounded-2xl border border-amber-500/15 p-6 mb-6"
          style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.07), var(--ax-card-bg-soft))' }}>
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="h-4 w-4 text-amber-300" />
            <span className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase">
              {t.format === 'knockout' ? 'KNOCKOUT' : t.format === 'round_robin' ? 'ROUND-ROBIN LEAGUE' : 'SEASONAL SPLIT'}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-100 leading-tight mb-2"
            data-testid="arenax-tournament-name">{t.name}</h1>
          {t.season_label && (
            <p className="text-sm text-pink-300 font-bold uppercase tracking-wider mb-3">{t.season_label}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.locality ? `${t.locality}, ` : ''}{t.city}</span>
            <span className="inline-flex items-center gap-1"><Flame className="h-3 w-3" /> {t.sport}</span>
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {t.registered_count}/{t.max_squads} squads</span>
            {t.starts_on && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {t.starts_on}</span>}
            {canViewHistory && (
              <button
                onClick={() => setAuditOpen(true)}
                data-testid="arenax-tournament-history-btn"
                className="inline-flex items-center gap-1 ml-auto text-[10px] uppercase tracking-wider font-bold text-slate-300 hover:text-white px-2 py-1 rounded-md border border-slate-500/30 hover:bg-slate-500/10 transition-colors"
                title="View tournament lifecycle history"
              >
                <History className="h-3 w-3" />History
              </button>
            )}
          </div>

          {/* Defending Champions banner */}
          {t.defending_champion_squad_name && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10"
              data-testid="arenax-tournament-defending-banner">
              <Crown className="h-4 w-4 text-amber-300" />
              <div>
                <div className="text-[9px] uppercase tracking-wider text-amber-300/70 font-bold">Defending Champions</div>
                <div className="text-sm font-bold text-slate-100">{t.defending_champion_squad_name}</div>
              </div>
            </div>
          )}

          {/* Active champion (if completed) */}
          {t.current_champion_squad_name && t.status === 'completed' && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10"
              data-testid="arenax-tournament-champion-banner">
              <Crown className="h-4 w-4 text-emerald-300" />
              <div>
                <div className="text-[9px] uppercase tracking-wider text-emerald-300/70 font-bold">Champion</div>
                <div className="text-sm font-bold text-slate-100">{t.current_champion_squad_name}</div>
              </div>
            </div>
          )}

          {/* Phase 2J — Auto-composed Crew Recap (tribal narrative) */}
          {recap && recap.sentences?.length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-500/20 p-4 bg-amber-500/5"
              data-testid="arenax-tournament-recap">
              <div className="text-[9px] uppercase tracking-wider text-amber-300 font-bold mb-2">Tournament Recap</div>
              <div className="space-y-2">
                {recap.sentences.map((s, i) => (
                  <p key={i} className="text-sm text-slate-100 leading-snug" data-testid={`arenax-recap-sentence-${i}`}>{s}</p>
                ))}
              </div>
            </div>
          )}

          {/* Action: Join as squad */}
          {canJoin && (
            <div className="mt-5 flex items-center gap-2">
              <select value={selectedSquadId || ''} onChange={e => setSelectedSquadId(e.target.value)}
                data-testid="arenax-tournament-squad-picker"
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-200 bg-slate-900 border border-slate-700 focus:outline-none focus:border-cyan-500/60">
                {mySquads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <button onClick={onJoin} disabled={joining}
                data-testid="arenax-tournament-join-btn"
                className="px-4 py-1.5 rounded-lg text-xs font-bold text-slate-950 bg-gradient-to-r from-amber-300 to-pink-400 hover:brightness-110 transition-all disabled:opacity-50">
                {joining ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Register Squad'}
              </button>
            </div>
          )}
          {alreadyRegistered && t.status === 'registration' && (
            <p className="mt-4 text-xs text-emerald-300 font-bold">✓ Your squad is registered.</p>
          )}

          {canGenerate && (
            <div className="mt-5">
              <button onClick={onGenerateBracket} disabled={generating}
                data-testid="arenax-tournament-generate-bracket-btn"
                className="px-4 py-2 rounded-lg text-sm font-bold text-slate-950 bg-gradient-to-r from-cyan-300 to-emerald-300 hover:brightness-110 transition-all disabled:opacity-50">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : `🔥 Lock the bracket (${t.registered_count} squads)`}
              </button>
            </div>
          )}
        </div>

        {/* Squads list */}
        <div className="rounded-2xl border border-slate-800 p-5 mb-6"
          data-testid="arenax-tournament-squads"
          style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}>
          <h3 className="text-sm font-bold text-slate-100 mb-3">Registered Squads</h3>
          {t.squads?.length === 0 ? (
            <p className="text-xs text-slate-500">No squads registered yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
              {t.squads?.map(s => (
                <div key={s.id} data-testid={`arenax-tournament-squad-${s.squad_id}`}
                  className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 flex items-center gap-2">
                  <span className="text-[10px] font-bold text-amber-300 tabular-nums">#{s.seed}</span>
                  <span className="text-sm font-bold text-slate-100 truncate flex-1">{s.squad_name}</span>
                  {s.status === 'champion' && <Crown className="h-3.5 w-3.5 text-emerald-300" />}
                  {s.status === 'eliminated' && <span className="text-[9px] text-slate-500 font-bold uppercase">OUT</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Phase 2K PR 3 — Comeback of the Split callout (rare-by-design) */}
        {t.status === 'completed' && (
          <div className="mb-4">
            <ArenaxComebackCallout tournamentId={t.id} />
          </div>
        )}

        {/* Phase 2M PR 1 — Editorial retrospective (renders only if persisted) */}
        {t.status === 'completed' && (
          <div className="mb-4">
            <ArenaxStoryInline tournamentId={t.id} kind="season_retrospective" />
          </div>
        )}

        {/* Bracket / Standings */}
        {t.format === 'knockout' && t.matches?.length > 0 && <BracketView matches={t.matches} t={t} canRecord={isCreator || mySquads.length > 0} onRecorded={reload} />}
        {(t.format === 'round_robin' || t.format === 'league_split') && t.standings?.length > 0 && (
          <StandingsView t={t} canRecord={isCreator || mySquads.length > 0} onRecorded={reload} />
        )}
      </div>

      {/* Phase 2J Realtime Phase 3 — Spectator reaction stream
          Subscribes to the tournament channel for bracket-wide reaction broadcasts. */}
      <ArenaxSpectatorStream incomingReaction={rt.lastReaction} />

      {/* StreamX Sprint 3c — Tournament lifecycle history */}
      <TournamentAuditDrawer
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        entity={t}
      />
    </ArenaxLayout>
  );
}

function BracketView({ matches, t, canRecord, onRecorded }) {
  // Phase 2J Realtime Phase 3 — track newly-filled squad slots so we can flash
  // the advancing winner card briefly on auto-advance.
  const prevMatchesRef = useRef({});
  const justAdvancedRef = useRef(new Set());
  useEffect(() => {
    const next = {};
    const advanced = new Set();
    matches.forEach(m => {
      const prev = prevMatchesRef.current[m.id];
      // A slot was just populated → advance flash
      if (prev) {
        if (!prev.squad_a_id && m.squad_a_id) advanced.add(m.id);
        if (!prev.squad_b_id && m.squad_b_id) advanced.add(m.id);
      }
      next[m.id] = { squad_a_id: m.squad_a_id, squad_b_id: m.squad_b_id };
    });
    prevMatchesRef.current = next;
    justAdvancedRef.current = advanced;
    if (advanced.size > 0) {
      // Clear after animation completes
      const tid = setTimeout(() => { justAdvancedRef.current = new Set(); }, 2200);
      return () => clearTimeout(tid);
    }
  }, [matches]);

  const rounds = [...new Set(matches.map(m => m.round_number))].sort((a, b) => a - b);
  return (
    <div className="rounded-2xl border border-slate-800 p-5"
      data-testid="arenax-tournament-bracket"
      style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.12), var(--ax-card-bg-soft))' }}>
      <h3 className="text-sm font-bold text-slate-100 mb-4 flex items-center gap-2">
        <Trophy className="h-4 w-4 text-amber-300" /> Bracket
      </h3>
      <div className="overflow-x-auto">
        <div className="inline-flex gap-6 min-w-full">
          {rounds.map(r => (
            <div key={r} className="flex flex-col gap-4 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                {r === rounds[rounds.length - 1] ? 'Final' : r === rounds[rounds.length - 2] && rounds.length > 1 ? 'Semis' : `Round ${r}`}
              </div>
              {matches.filter(m => m.round_number === r).map(m => (
                <MatchCard key={m.id} match={m} t={t} canRecord={canRecord} onRecorded={onRecorded}
                  justAdvanced={justAdvancedRef.current.has(m.id)} />
              ))}
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes arenax-advance-flash {
          0%   { box-shadow: 0 0 0 0 rgba(245,158,11,0); transform: scale(1); }
          25%  { box-shadow: 0 0 24px 4px rgba(245,158,11,0.45); transform: scale(1.025); }
          100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); transform: scale(1); }
        }
        .arenax-match-advanced { animation: arenax-advance-flash 2s ease-out; }
      `}</style>
    </div>
  );
}

function StandingsView({ t, canRecord, onRecorded }) {
  // Phase 2J Realtime Phase 3 — animate rank changes when standings reorder.
  // Tracks each squad's previous rank to apply a brief flash + slide animation.
  const prevRanksRef = useRef({});
  useEffect(() => {
    const next = {};
    (t.standings || []).forEach(r => { next[r.squad_id] = r.rank; });
    prevRanksRef.current = next;
  }, [t.standings]);
  const prevRanks = prevRanksRef.current;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-800 p-5"
        data-testid="arenax-tournament-standings"
        style={{ background: 'linear-gradient(160deg, rgba(16,185,129,0.04), var(--ax-card-bg-soft))' }}>
        <h3 className="text-sm font-bold text-slate-100 mb-4 flex items-center gap-2">
          <Trophy className="h-4 w-4 text-emerald-300" /> Standings
        </h3>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <th className="text-left py-2 pr-2">#</th>
              <th className="text-left py-2">Squad</th>
              <th className="text-right py-2 px-1">P</th>
              <th className="text-right py-2 px-1">W</th>
              <th className="text-right py-2 px-1">D</th>
              <th className="text-right py-2 px-1">L</th>
              <th className="text-right py-2 pl-1">Pts</th>
            </tr>
          </thead>
          <tbody>
            {t.standings.map(row => {
              const prev = prevRanks[row.squad_id];
              const rankDelta = prev != null && prev !== row.rank ? prev - row.rank : 0;
              const rose = rankDelta > 0;
              const fell = rankDelta < 0;
              return (
                <tr key={row.squad_id}
                  className={`border-t border-slate-800 transition-all duration-700 ease-out ${
                    rose ? 'arenax-rank-rose' : fell ? 'arenax-rank-fell' : ''
                  }`}
                  data-testid={`arenax-tournament-standing-row-${row.squad_id}`}>
                  <td className="py-2 pr-2 text-slate-500 font-bold relative">
                    {row.rank}
                    {rankDelta !== 0 && (
                      <span
                        className={`ml-1 text-[9px] font-black tabular-nums ${rose ? 'text-emerald-300' : 'text-rose-300'}`}
                        data-testid={`arenax-rank-delta-${row.squad_id}`}>
                        {rose ? `▲${rankDelta}` : `▼${Math.abs(rankDelta)}`}
                      </span>
                    )}
                  </td>
                  <td className="py-2 font-bold text-slate-100">{row.squad_name}</td>
                  <td className="py-2 px-1 text-right tabular-nums text-slate-300">{row.played}</td>
                  <td className="py-2 px-1 text-right tabular-nums text-emerald-300">{row.wins}</td>
                  <td className="py-2 px-1 text-right tabular-nums text-slate-400">{row.draws}</td>
                  <td className="py-2 px-1 text-right tabular-nums text-slate-500">{row.losses}</td>
                  <td className="py-2 pl-1 text-right tabular-nums font-black text-amber-300">{row.points}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <style>{`
          @keyframes arenax-rank-rise {
            0%   { background: rgba(16,185,129,0); }
            30%  { background: rgba(16,185,129,0.18); }
            100% { background: rgba(16,185,129,0); }
          }
          @keyframes arenax-rank-fall {
            0%   { background: rgba(244,63,94,0); }
            30%  { background: rgba(244,63,94,0.15); }
            100% { background: rgba(244,63,94,0); }
          }
          .arenax-rank-rose { animation: arenax-rank-rise 1.6s ease-out; }
          .arenax-rank-fell { animation: arenax-rank-fall 1.6s ease-out; }
        `}</style>
      </div>

      {/* Match list — same MatchCard component */}
      <div className="rounded-2xl border border-slate-800 p-5"
        style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.10), var(--ax-card-bg-soft))' }}>
        <h3 className="text-sm font-bold text-slate-100 mb-4">Fixtures</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {t.matches?.map(m => <MatchCard key={m.id} match={m} t={t} canRecord={canRecord} onRecorded={onRecorded} />)}
        </div>
      </div>
    </div>
  );
}

function MatchCard({ match, t, canRecord, onRecorded, justAdvanced = false }) {
  const [editing, setEditing] = useState(false);
  const [sa, setSa] = useState(match.score_a ?? '');
  const [sb, setSb] = useState(match.score_b ?? '');
  const [saving, setSaving] = useState(false);

  // Phase 2I — per-match realtime channel (live score + minute + reactions)
  const matchRt = useArenaxRealtime({ channel: 'match', channelId: match.id, enabled: !!match.id });
  const liveSnapshot = matchRt.snapshot;
  const liveProgress = matchRt.matchProgress;

  const submit = async () => {
    if (sa === '' || sb === '') { toast.error('Both scores needed'); return; }
    setSaving(true);
    try {
      await api.post(`/arenax/tournaments/${t.id}/matches/${match.id}/result`, {
        score_a: Number(sa), score_b: Number(sb),
      });
      toast.success('Result locked');
      setEditing(false);
      onRecorded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit result');
    } finally { setSaving(false); }
  };

  // Live-data resolution: realtime > persisted (match doc)
  const liveScoreA = liveProgress?.score_a ?? liveProgress?.live_score_a ??
    liveSnapshot?.score_a ?? match.live_score_a ?? match.score_a;
  const liveScoreB = liveProgress?.score_b ?? liveProgress?.live_score_b ??
    liveSnapshot?.score_b ?? match.live_score_b ?? match.score_b;
  const liveMinute = liveProgress?.live_minute ?? liveProgress?.minute ??
    liveSnapshot?.live_minute ?? match.live_minute;
  const liveStatus = liveProgress?.live_status ?? liveSnapshot?.live_status ?? match.live_status;
  const isLive = liveStatus === 'live' || liveStatus === 'halftime';

  const winnerA = match.winner_squad_id === match.squad_a_id;
  const winnerB = match.winner_squad_id === match.squad_b_id;

  return (
    <div className={`rounded-lg border p-3 transition-all ${
        isLive ? 'border-amber-500/40 bg-amber-500/5 shadow-lg shadow-amber-500/10' : 'border-slate-800 bg-slate-900/40'
      } ${justAdvanced ? 'arenax-match-advanced' : ''}`}
      data-testid={`arenax-tournament-match-${match.id}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">
          R{match.round_number} · Match {match.match_index}
        </span>
        <div className="flex items-center gap-1.5">
          {isLive && (
            <span data-testid={`arenax-match-live-${match.id}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider text-amber-100 bg-amber-500/30 border border-amber-400/50">
              <Radio className="h-2.5 w-2.5 animate-pulse" />
              LIVE{liveMinute != null ? ` · ${liveMinute}'` : ''}
            </span>
          )}
          {isLive && <ArenaxLiveViewers matchId={match.id} threshold={1} compact />}
          <span className={`text-[9px] uppercase tracking-wider font-bold ${match.status === 'completed' ? 'text-emerald-300' : 'text-slate-500'}`}>
            {match.status}
          </span>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className={`flex items-center justify-between text-sm transition-all ${winnerA ? 'font-black text-amber-300' : 'text-slate-200'}`}>
          <span className="truncate">{match.squad_a_name || <span className="italic text-slate-500">— TBD —</span>}</span>
          <span className={`tabular-nums ${isLive ? 'font-black text-amber-200' : ''}`}>{liveScoreA ?? '—'}</span>
        </div>
        <div className={`flex items-center justify-between text-sm transition-all ${winnerB ? 'font-black text-amber-300' : 'text-slate-200'}`}>
          <span className="truncate">{match.squad_b_name || <span className="italic text-slate-500">— TBD —</span>}</span>
          <span className={`tabular-nums ${isLive ? 'font-black text-amber-200' : ''}`}>{liveScoreB ?? '—'}</span>
        </div>
      </div>
      {liveProgress?.last_live_note && (
        <p className="text-[11px] text-amber-300 mt-1.5 font-semibold animate-pulse">
          {liveProgress.last_live_note}
        </p>
      )}
      {/* Phase 2K PR 1 — Rivalry hint surfaces the narrative when these two squads
          have prior history. Renders compact chips + a one-liner anticipation copy. */}
      {match.rivalry_hint && match.squad_a_id && match.squad_b_id && (
        <ArenaxRivalryBadge
          hint={match.rivalry_hint}
          chip={match.rivalry_h2h_chip}
          streakChip={match.rivalry_streak_chip}
          testId={`arenax-tournament-match-rivalry-${match.id}`}
        />
      )}
      {canRecord && match.status !== 'completed' && match.squad_a_id && match.squad_b_id && !editing && (
        <button onClick={() => setEditing(true)}
          data-testid={`arenax-tournament-match-edit-${match.id}`}
          className="mt-2 text-[10px] text-cyan-300 hover:text-cyan-200 font-bold flex items-center gap-1">
          Enter result <ChevronRight className="h-3 w-3" />
        </button>
      )}
      {editing && (
        <div className="mt-2 flex items-center gap-1.5">
          <input type="number" placeholder="A" value={sa} onChange={e => setSa(e.target.value)}
            className="w-12 px-2 py-1 rounded-md text-xs bg-slate-950 border border-slate-700 text-slate-100 focus:border-cyan-500/60 focus:outline-none" />
          <span className="text-slate-500 text-xs">vs</span>
          <input type="number" placeholder="B" value={sb} onChange={e => setSb(e.target.value)}
            className="w-12 px-2 py-1 rounded-md text-xs bg-slate-950 border border-slate-700 text-slate-100 focus:border-cyan-500/60 focus:outline-none" />
          <button onClick={submit} disabled={saving}
            className="px-2 py-1 rounded-md text-[10px] font-bold text-slate-950 bg-amber-300 hover:brightness-110 disabled:opacity-50">
            {saving ? '...' : 'Save'}
          </button>
          <button onClick={() => setEditing(false)} className="text-[10px] text-slate-500 hover:text-slate-300">cancel</button>
        </div>
      )}
      {/* Reactions dock — only on live or completed matches with real squads */}
      {match.squad_a_id && match.squad_b_id && (
        <div className="mt-2 flex items-center justify-end gap-2">
          {isLive && (
            <Link to={`/arenax/matches/${match.id}/spectate`}
              data-testid={`arenax-tournament-match-watch-${match.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider text-cyan-100 bg-cyan-500/20 border border-cyan-400/45 hover:bg-cyan-500/30 transition-colors">
              <Eye className="h-2.5 w-2.5" />
              Watch
            </Link>
          )}
          <ArenaxReactionDock targetType="match" targetId={match.id}
            incomingReaction={matchRt.lastReaction} />
        </div>
      )}
    </div>
  );
}
